import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History, RefreshCw, Eye, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockTransferService } from '../../services/stockTransferService';
import { StockTransferListDTO, StockTransferStatus } from '../../types';
import './StockTransferPage.css';

const STATUS_LABEL: Partial<Record<StockTransferStatus, string>> = {
  Completed: 'Completed',
  Cancelled: 'Cancelled',
};

const STATUS_CLASS: Partial<Record<StockTransferStatus, string>> = {
  Completed: 'ims-badge st-badge-completed',
  Cancelled: 'ims-badge st-badge-cancelled',
};

const StockTransferHistoryPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [transfers,  setTransfers]  = useState<StockTransferListDTO[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stockTransferService.getAll(1, 500, 'Completed,Cancelled');
      setTransfers(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await stockTransferService.getAll(1, 500, 'Completed,Cancelled');
      setTransfers(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  // ── Stat counts ────────────────────────────────────────────────────────────
  const completedCount = transfers.filter(t => t.status === 'Completed').length;
  const cancelledCount = transfers.filter(t => t.status === 'Cancelled').length;

  const columns: GridColumn[] = [
    { key: 'transferNumber',          label: t('stockTransfer.transferNumber'),   sortable: true, width: '160px', render: (v) => <code>{v as string}</code> },
    { key: 'transferInNumber',        label: t('stockTransfer.transferInNumber'), width: '160px', render: (v) => (v as string) || '—' },
    {
      key: 'transferDate',
      label: t('stockTransfer.transferDate'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    { key: 'sourceWarehouseName',      label: t('stockTransfer.source'),      sortable: true, width: '150px' },
    { key: 'destinationWarehouseName', label: t('stockTransfer.destination'),  sortable: true, width: '150px' },
    {
      key: 'status',
      label: t('common.status'),
      sortable: true,
      width: '120px',
      render: (v) => {
        const s = v as StockTransferStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    { key: 'itemCount',       label: t('stockTransfer.items'),       width: '80px' },
    { key: 'requestedByName', label: t('stockTransfer.requestedBy'), width: '140px' },
    {
      key: 'createdAt',
      label: t('common.createdAt'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('common.view'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="st-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <div className="ims-list-header-left">
            <h1 className="ims-list-title">
              <History size={22} strokeWidth={1.75} style={{ marginRight: 8 }} />
              {t('stockTransfer.transferHistory')}
            </h1>
            <p className="ims-list-subtitle">{t('stockTransfer.transferHistoryDesc')}</p>
          </div>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon st-stat-icon-done">
              <CheckCircle2 size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{completedCount}</span>
              <span className="ims-stat-label">{t('stockTransfer.completed')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon st-stat-icon-pending" style={{ background: 'var(--ims-bg-page)', color: 'var(--ims-raw-gray-500)', border: '1px solid var(--ims-border)' }}>
              <XCircle size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{cancelledCount}</span>
              <span className="ims-stat-label">{t('stockTransfer.cancelled', 'Cancelled')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={transfers}
            loading={loading}
            actions={actions}
            pageSize={10}
            searchPlaceholder={t('common.search', 'Search…')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={t('stockTransfer.noHistory')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockTransferHistoryPage;
